/* -------------------------------------------------------------------------- */
/* REGISTER MIXIN                                                             */
/* -------------------------------------------------------------------------- */
import { v4 as uuid } from 'uuid';
import { snakeToCamel } from '@nodes/helpers/string';
import YRecaptcha from '@components/Recaptcha';
import YFormTitle from '@rogues/Auth/blocks/FormTitle';

export default {
    name: 'RegisterMixin',

    components: {
        YRecaptcha,
        YFormTitle,
    },

    props: {
        /**
         * Color of the links, buttons and forms
         */
        color: String,

        /**
         * Form Title
         */
        formTitle: String,

        /**
         * Form Description
         */
        formDescription: String,

        /**
         * Form Description Link, shows at the end of the form description
         * Used Types: Modern
         * Sample: {
         * `    title: '',
         * `    route: {
         * `        name: '', params: {}, query: {},
         * `    }
         * `}
         * }
         */
        formDescriptionLink: Object,

        /**
         * Service Name
         */
        service: {
            type   : String,
            default: 'Person',
        },

        /**
         * Service Endpoint Name
         */
        serviceName: {
            type   : String,
            default: 'register',
        },

        /**
         * Redirect Route after success register
         */
        redirectRouteName: String,

        /**
         * Login user after register
         */
        loginAfterRegister: Boolean,

        /**
         * After Success register Action
         */
        afterSuccessRegisterAction: Function,
    },

    /**
     * @inheritdoc
     */
    data() {
        return {
            keyHelper        : uuid(),
            model            : {},
            configs          : null,
            formElementsArray: [],
            recaptcha        : {
                active: false,
            },
        };
    },

    /**
     * @inheritdoc
     */
    created() {
        this.fetchRegisterFormConfig();
    },

    methods: {
        /**
         * Fetch register form config
         *
         * @returns {Promise}
         */
        fetchRegisterFormConfig() {
            this.$wait.start('fetch-config');
            return this.$services.Person.userRegisterForm({
                slug: this.$config('auth.register.role'),
            }).then((response) => {
                this.configs = response.data.metadata;
                this.formElements(this.configs.fields[0].fields);
                this.setRecaptchaType();
            }).catch((error) => {
                this.handleError(error);
            }).finally(() => {
                this.$wait.end('fetch-config');
            });
        },

        /**
         * Create Form Elements Object
         *
         * @param {object} configs
         */
        formElements(configs) {
            const numberTypes = ['mobile', 'phone', 'code_melli'];
            Object.keys(configs).forEach((key) => {
                const item = configs[key];
                // Define type
                let type = 'text';
                if (item.type === 'password') {
                    type = 'password';
                } else if (numberTypes.includes(item.type)) {
                    type = 'number';
                }
                const format = item.type === 'mobile' ? '(DDDD) DDD DDDD' : null;
                const validation = item.type === 'email' ? '|email' : '';
                this.formElementsArray.push({
                    component_name : 'input',
                    id             : `${snakeToCamel(item.slug)}-${this.keyHelper * key}`,
                    order          : item.order,
                    label          : item.title,
                    placeholder    : item.title,
                    validation     : item.required ? `required${validation}` : validation,
                    name           : item.slug,
                    component_class: 'mb-10',
                    type,
                    format,
                });
            });
        },

        /**
         * Register User
         */
        register() {
            if (this.isDev || this.recaptcha.size !== 'invisible') {
                this.doRegister();
            } else {
                this.verifyRecaptcha();
            }
        },

        /**
         * Set recaptcha type
         */
        setRecaptchaType() {
            const recaptcha = this.get(this.configs, 'recaptcha');

            if (recaptcha.active) {
                this.recaptcha = {
                    active: true,
                    key   : recaptcha.key,
                    size  : recaptcha.size || 'invisible',
                };
            }
        },
        
        /**
         * Verify recaptcha token
         */
        verifyRecaptcha() {
            this.$refs.recaptcha.verify();
        },
        
        /**
         * Set token and start register
         * (set in recaptcha field)
         *
         * @param {string} token
         */
        beforeRegister(token) {
            this.model['g-recaptcha-response'] = token;
            if (this.recaptcha.size === 'invisible') {
                this.doRegister();
            }
        },

        /**
         * Register new user
         *
         * @returns {*}
         */
        doRegister() {
            this.$wait.start('register-form');
            return this.$services[this.service][this.serviceName]({
                role: this.$config('auth.register.role'),
                ...this.model,
            }).then((response) => {
                this.token = response.data.results.access_token;
                this.$toast.success(this.$t('messages.register.success'));
                if (this.loginAfterRegister) {
                    this.$store.commit('auth/login', this.token);
                }
                this.afterSuccessRegister(response);
            }).catch((error) => {
                if (this.recaptcha.active && this.recaptcha.size === 'invisible') {
                    this.$refs.recaptcha.reset();
                }
                this.handleError(error, this.$t('messages.login.error'));
            }).finally(() => {
                this.$wait.end('register-form');
            });
        },

        /**
         * Custom register action
         * Should be overwritten in register page
         */
        afterSuccessRegister() {
            if (this.afterSuccessRegisterAction) {
                this.afterSuccessRegisterAction();
                this.setRedirect();
            } else if (this.redirectRouteName) {
                this.$router.push({ name: this.redirectRouteName });
            } else if (this.$store.getters['auth/redirect']?.name) {
                this.setRedirect();
            } else {
                this.$router.push({ name: 'home' });
            }
        },

        /**
         * Set Redirect
         */
        setRedirect() {
            if (this.$store.getters['auth/redirect']?.name) {
                this.$router.push(this.$store.getters['auth/redirect']);
                this.$store.dispatch('auth/resetRedirect');
                localStorage.removeItem('redirect');
            }
        },
    },
};

